import { useState, useEffect, useMemo, Component, type ReactNode, type ErrorInfo } from "react";
import { useUser } from "@clerk/react";
import { useLocation } from "wouter";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Users, BookOpen, FileBox, CheckSquare, TrendingUp, CreditCard,
  Library, GraduationCap, RefreshCw, ChevronUp, ChevronDown, Minus,
  Building2, Plus, Trash2, Copy, CheckCircle2, Pencil, Lock, Unlock,
  ShieldCheck, ShieldOff, X, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { customFetch, ApiError } from "@workspace/api-client-react";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  useAdminCheck,
  useAdminOverview,
  useAdminUsers,
  useAdminSignups,
  useAdminActivity,
  useAdminRecordTypes,
  useAdminSubscriptions,
  useAdminFeatureAdoption,
  useAdminSchoolDetails,
  useDeleteUser,
  useAdminCostOverview,
  useAdminRecentAiComments,
  useAdminFeatureCounters,
  useAdminFeatureRequests,
  useAdminDeletedAccounts,
  useGrantBetaAccess,
  useRevokeBetaAccess,
  useRestoreAccount,
  type AdminDeletedAccount,
} from "@/hooks/useAdmin";
import { useToast } from "@/hooks/use-toast";
import { Search, Stethoscope, ChevronRight } from "lucide-react";

// ─── Error boundary ────────────────────────────────────────────────────────────

class AdminErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AdminPage] render error:", error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-8 text-center">
          <p className="text-red-600 font-semibold">Something went wrong loading the admin panel.</p>
          <p className="text-sm text-gray-500 font-mono break-all">{this.state.error.message}</p>
          <button
            className="mt-2 px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const COLORS = ["#1cb08a", "#378add", "#ef9f27", "#76d0b9", "#87b9ea", "#6366f1"];

function StatCard({
  icon: Icon, label, value, sub, color = "text-primary",
}: {
  icon: React.ElementType; label: string; value: string | number; sub?: string; color?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
      <div className={`p-2.5 rounded-lg bg-gray-50 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm font-medium text-gray-600">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SortIcon({ col, sortCol, sortDir }: { col: string; sortCol: string; sortDir: string }) {
  if (col !== sortCol) return <Minus className="h-3 w-3 text-gray-300 ml-1" />;
  return sortDir === "asc"
    ? <ChevronUp className="h-3 w-3 text-primary ml-1" />
    : <ChevronDown className="h-3 w-3 text-primary ml-1" />;
}

const SUB_BADGE: Record<string, string> = {
  active:   "bg-green-100 text-green-700",
  trialing: "bg-blue-100 text-blue-700",
  past_due: "bg-amber-100 text-amber-700",
  canceled: "bg-red-100 text-red-700",
  free:     "bg-gray-100 text-gray-500",
};

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" });
}

const STATUS_LABELS: Record<string, string> = {
  active:   "Active",
  trialing: "Trialling",
  past_due: "Past due",
  canceled: "Cancelled",
  free:     "Free",
};

function fmtStatus(status: string | null | undefined): string {
  if (!status) return "Free";
  return STATUS_LABELS[status] ?? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminPage() {
  return <AdminErrorBoundary><AdminPageInner /></AdminErrorBoundary>;
}

// ─── Schools Admin Panel (Task #44) ──────────────────────────────────────────
// Admin-only panel that mints, edits, and revokes school codes. Replaces the
// old "School Code Management" form. Codes are now generated server-side; the
// admin sets the school's display name, seat limit, and (optionally) email
// domain lock at mint time, and can edit each of those + status afterwards.

type AdminSchool = {
  id: number;
  schoolCode: string;
  displayName: string;
  // headTeacherPin is only present in the POST /admin/schools mint response
  // (shown ONCE). The GET /admin/schools list never returns it — operators
  // must capture it at mint time.
  headTeacherPin?: string;
  createdAt: string;
  seatLimit: number | null;
  allowedEmailDomains: string[];
  domainLockEnabled: boolean;
  status: "active" | "revoked";
  teacherCount: number;
  headTeacherCount: number;
  founderUserId: string | null;
  founderName: string | null;
  // Latest record/diary write across all teachers in this school. Server
  // returns 1970-01-01 ('epoch') when there's no activity yet — treat any
  // pre-1971 date as null.
  lastActivityAt: string | null;
};

type SchoolDetailFlag =
  | "no_school_name"
  | "no_classes"
  | "duplicate_students"
  | "trial_expiring"
  | "teachers_with_no_classes";

type SchoolDetailTeacher = {
  userId: string;
  name: string;
  email: string | null;
  schoolRole: string;
  joinedAt: string;
  lastSeenAt: string | null;
  lastRecordAt: string | null;
  lastDiaryAt: string | null;
  classesCount: number;
  subscriptionStatus: string;
  subscriptionEnds: string | null;
};

type SchoolDetail = {
  school: {
    schoolCode: string;
    displayName: string;
    schoolName: string | null;
    seatLimit: number | null;
    allowedEmailDomains: string[];
    domainLockEnabled: boolean;
    status: "active" | "revoked";
    createdAt: string;
  };
  setup: {
    schoolNameSet: boolean;
    classesCount: number;
    coursesCount: number;
    studentsCount: number;
    duplicateStudentSuggestions: number;
    teachersWithNoClasses: number;
  };
  teachers: SchoolDetailTeacher[];
  flags: SchoolDetailFlag[];
};

type MintSchoolPayload = {
  displayName: string;
  seatLimit: number;
  allowedEmailDomains: string[];
  domainLockEnabled: boolean;
};

type PatchSchoolPayload = Partial<{
  displayName: string;
  seatLimit: number;
  allowedEmailDomains: string[];
  domainLockEnabled: boolean;
  status: "active" | "revoked";
}>;

/**
 * Pull a friendly message out of an ApiError's typed JSON body, with fallback.
 * Reads `err.data.error` directly from the structured `ApiError.data` (Task #85)
 * — never JSON-parses `Error.message`. Re-exported via `lib/api-error.ts`.
 */
function extractApiError(err: unknown, fallback: string): string {
  return getApiErrorMessage(err, fallback);
}

function SchoolsAdminPanel() {
  const queryClient = useQueryClient();
  const [mintOpen, setMintOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState<AdminSchool | null>(null);
  const [mintedSchool, setMintedSchool] = useState<AdminSchool | null>(null);

  const schools = useQuery<AdminSchool[]>({
    queryKey: ["admin", "schools"],
    queryFn: () => customFetch<AdminSchool[]>("/api/admin/schools", { credentials: "include" }),
  });

  const deleteSchool = useMutation({
    mutationFn: (code: string) =>
      customFetch(`/api/admin/schools/${encodeURIComponent(code)}`, {
        method: "DELETE",
        credentials: "include",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "schools"] }),
    onError: (err: unknown) => {
      const msg = extractApiError(err, "Delete failed");
      alert(msg);
    },
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" /> Schools
          {schools.data && (
            <Badge variant="secondary" className="ml-2">{schools.data.length}</Badge>
          )}
        </h2>
        <Button onClick={() => setMintOpen(true)} className="gap-1.5" size="sm">
          <Plus className="h-4 w-4" />
          Add school
        </Button>
      </div>

      {schools.isLoading ? (
        <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
      ) : schools.isError ? (
        <p className="text-sm text-red-400 py-4 text-center">Could not load schools.</p>
      ) : !schools.data || schools.data.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">No schools set up yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2.5 text-left">School</th>
                <th className="px-4 py-2.5 text-left">Code</th>
                <th className="px-4 py-2.5 text-left">Founding HT</th>
                <th className="px-4 py-2.5 text-center">Seats</th>
                <th className="px-4 py-2.5 text-left">Domains</th>
                <th className="px-4 py-2.5 text-center">Status</th>
                <th className="px-4 py-2.5 text-left">Created</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {schools.data.map((s) => {
                const seatPct = s.seatLimit ? Math.min(100, Math.round((s.teacherCount / s.seatLimit) * 100)) : 0;
                const isFull = s.seatLimit !== null && s.teacherCount >= s.seatLimit;
                return (
                  <tr key={s.schoolCode} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{s.displayName}</p>
                      <p className="text-xs text-gray-400">{s.headTeacherCount} HT · {s.teacherCount} total</p>
                    </td>
                    <td className="px-4 py-3">
                      <CopyableCode label={s.schoolCode} value={s.schoolCode} className="font-bold text-gray-900" />
                      {/* PIN intentionally not shown here — captured once at mint time only. */}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      {s.founderName ? (
                        <span title={s.founderUserId ?? ""}>{s.founderName}</span>
                      ) : (
                        <span className="italic text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="inline-flex flex-col items-center gap-1">
                        <span className={`text-xs font-semibold ${isFull ? "text-rose-700" : "text-gray-700"}`}>
                          {s.teacherCount}{s.seatLimit !== null ? ` / ${s.seatLimit}` : ""}
                        </span>
                        {s.seatLimit !== null && (
                          <div className="h-1 w-16 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${isFull ? "bg-rose-500" : seatPct >= 80 ? "bg-amber-500" : "bg-teal-500"}`}
                              style={{ width: `${seatPct}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {s.domainLockEnabled
                          ? <Lock className="h-3.5 w-3.5 text-teal-600" aria-label="Locked" />
                          : <Unlock className="h-3.5 w-3.5 text-gray-300" aria-label="Unlocked" />}
                        <span className="text-xs text-gray-500">
                          {s.allowedEmailDomains.length === 0
                            ? <span className="italic">none</span>
                            : s.allowedEmailDomains.length === 1
                              ? s.allowedEmailDomains[0]
                              : `${s.allowedEmailDomains[0]} +${s.allowedEmailDomains.length - 1}`}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={s.status === "active"
                        ? "bg-green-100 text-green-700 border-0"
                        : "bg-rose-100 text-rose-700 border-0"}>
                        {s.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(s.createdAt)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingSchool(s)}
                        className="text-gray-500 hover:text-primary"
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                        disabled={s.teacherCount > 0}
                        onClick={() => {
                          if (confirm(`Delete ${s.displayName} (${s.schoolCode})? This cannot be undone.`)) {
                            deleteSchool.mutate(s.schoolCode);
                          }
                        }}
                        title={s.teacherCount > 0 ? "Revoke instead — has joined teachers" : "Delete"}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {mintOpen && (
        <MintSchoolDialog
          onClose={() => setMintOpen(false)}
          onMinted={(school) => {
            setMintOpen(false);
            setMintedSchool(school);
            queryClient.invalidateQueries({ queryKey: ["admin", "schools"] });
          }}
        />
      )}

      {editingSchool && (
        <EditSchoolDialog
          school={editingSchool}
          onClose={() => setEditingSchool(null)}
          onSaved={() => {
            setEditingSchool(null);
            queryClient.invalidateQueries({ queryKey: ["admin", "schools"] });
          }}
        />
      )}

      {mintedSchool && (
        <MintedSchoolReveal school={mintedSchool} onClose={() => setMintedSchool(null)} />
      )}
    </div>
  );
}

// ─── School Troubleshooting Panel (Task #36) ─────────────────────────────────
// Read-only operator view aimed at "we can't get our school set up" support
// tickets. Lists every minted school with quick search + last-activity, then
// drills into a roster + setup snapshot + per-teacher activity + flagged
// stuck states. No actions (mints/edits/revokes) live here — those stay in
// the existing SchoolsAdminPanel above.

const FLAG_LABEL: Record<SchoolDetailFlag, { label: string; tone: string; description: string }> = {
  no_school_name: {
    label: "No school name",
    tone: "bg-amber-100 text-amber-800 border-amber-200",
    description: "School row exists but no display name has been saved.",
  },
  no_classes: {
    label: "No classes set up",
    tone: "bg-amber-100 text-amber-800 border-amber-200",
    description: "Nobody at this school has created any active classes.",
  },
  duplicate_students: {
    label: "Duplicate students detected",
    tone: "bg-rose-100 text-rose-800 border-rose-200",
    description: "Private students with names matching school-owned students.",
  },
  trial_expiring: {
    label: "Trial expiring soon",
    tone: "bg-blue-100 text-blue-800 border-blue-200",
    description: "At least one teacher's trial ends within 7 days.",
  },
  teachers_with_no_classes: {
    label: "Teachers with no classes",
    tone: "bg-gray-100 text-gray-700 border-gray-200",
    description: "Some teachers have joined but haven't set up any classes yet.",
  },
};

/** Treat the server's 'epoch' sentinel as null so the UI can show "—". */
function realDate(d: string | null | undefined): string | null {
  if (!d) return null;
  const ts = new Date(d).getTime();
  if (!Number.isFinite(ts)) return null;
  // Anything before 1971-01-01 is the "no activity" sentinel.
  if (ts < 31536000000) return null;
  return d;
}

function fmtDateTime(d: string | null | undefined): string {
  const real = realDate(d ?? null);
  if (!real) return "—";
  return new Date(real).toLocaleDateString("en-AU", {
    day: "numeric", month: "short", year: "2-digit",
  });
}

function SchoolTroubleshootingPanel({
  schools,
  isLoading,
  query,
  setQuery,
  onJumpToUser,
}: {
  schools: AdminSchool[];
  isLoading: boolean;
  query: string;
  setQuery: (q: string) => void;
  onJumpToUser: (userId: string) => void;
}) {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  // List is server-filtered; show all returned rows.
  const filtered = schools;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-primary" /> School troubleshooting
          <span className="text-xs font-normal text-gray-400 ml-1">
            Read-only setup &amp; activity view for support
          </span>
        </h2>
        <div className="relative w-full sm:w-72">
          <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code or school name…"
            className="pl-8 h-9"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">
          {query.trim() ? "No schools match your search." : "No schools yet."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2.5 text-left">School</th>
                <th className="px-4 py-2.5 text-left">Code</th>
                <th className="px-4 py-2.5 text-left">Founding HT</th>
                <th className="px-4 py-2.5 text-center">Seats</th>
                <th className="px-4 py-2.5 text-left">Last activity</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((s) => (
                <tr
                  key={s.schoolCode}
                  className={`hover:bg-gray-50 cursor-pointer ${
                    selectedCode === s.schoolCode ? "bg-blue-50/40" : ""
                  }`}
                  onClick={() =>
                    setSelectedCode((c) => (c === s.schoolCode ? null : s.schoolCode))
                  }
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{s.displayName}</p>
                    <p className="text-xs text-gray-400">
                      {s.headTeacherCount} HT · {s.teacherCount} total
                    </p>
                  </td>
                  <td className="px-4 py-3 font-mono font-semibold text-gray-800">
                    {s.schoolCode}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700">
                    {s.founderName ?? <span className="italic text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-700">
                    {s.teacherCount}
                    {s.seatLimit !== null ? ` / ${s.seatLimit}` : ""}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {fmtDateTime(s.lastActivityAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight
                      className={`h-4 w-4 text-gray-300 transition-transform ${
                        selectedCode === s.schoolCode ? "rotate-90" : ""
                      }`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedCode && (
        <SchoolDetailView
          code={selectedCode}
          onClose={() => setSelectedCode(null)}
          onJumpToUser={onJumpToUser}
        />
      )}
    </div>
  );
}

/** Wrapper that owns the search box state and queries `/admin/schools?q=…`
 *  server-side. The unfiltered list (q='') still uses the same query key
 *  as `SchoolsAdminPanel` so the cache is shared. */
function SchoolTroubleshootingPanelLoader({
  onJumpToUser,
}: {
  onJumpToUser: (userId: string) => void;
}) {
  const [query, setQuery] = useState("");
  // Cheap debounce: most search interactions are fast typists; 200ms is
  // imperceptible and prevents a request per keystroke without pulling in
  // a debounce library.
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const schools = useQuery<AdminSchool[]>({
    queryKey: debounced ? ["admin", "schools", debounced] : ["admin", "schools"],
    queryFn: () =>
      customFetch<AdminSchool[]>(
        debounced
          ? `/api/admin/schools?q=${encodeURIComponent(debounced)}`
          : "/api/admin/schools",
        { credentials: "include" },
      ),
  });
  return (
    <SchoolTroubleshootingPanel
      schools={schools.data ?? []}
      isLoading={schools.isLoading}
      query={query}
      setQuery={setQuery}
      onJumpToUser={onJumpToUser}
    />
  );
}

type RosterSortCol =
  | "name"
  | "schoolRole"
  | "subscriptionStatus"
  | "joinedAt"
  | "lastSeenAt"
  | "lastRecordAt"
  | "lastDiaryAt"
  | "classesCount";

function SchoolDetailView({
  code,
  onClose,
  onJumpToUser,
}: {
  code: string;
  onClose: () => void;
  onJumpToUser: (userId: string) => void;
}) {
  const detail = useAdminSchoolDetails(code);
  const data = detail.data as SchoolDetail | undefined;

  const [sortCol, setSortCol] = useState<RosterSortCol>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const toggleSort = (col: RosterSortCol) => {
    if (col === sortCol) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      // Sensible defaults: text columns ascending, numeric/date columns descending.
      const numericOrDate: RosterSortCol[] = [
        "joinedAt", "lastSeenAt", "lastRecordAt", "lastDiaryAt", "classesCount",
      ];
      setSortDir(numericOrDate.includes(col) ? "desc" : "asc");
    }
  };

  const sortedTeachers = useMemo(() => {
    if (!data) return [];
    const rows = [...data.teachers];
    const dir = sortDir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      const av = (a as any)[sortCol];
      const bv = (b as any)[sortCol];
      // Nulls always sort last regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [data, sortCol, sortDir]);

  return (
    <div className="mt-5 border border-gray-200 rounded-xl bg-gray-50/40 p-5">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            {data?.school.displayName ?? code}
            <span className="font-mono text-xs text-gray-500">{code}</span>
          </h3>
          {data?.school.schoolName && data.school.schoolName !== data.school.displayName && (
            <p className="text-xs text-gray-500 mt-0.5">School name: {data.school.schoolName}</p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {detail.isLoading ? (
        <p className="text-sm text-gray-400 py-4 text-center">Loading details…</p>
      ) : detail.isError || !data ? (
        <p className="text-sm text-red-500 py-4 text-center">Could not load school details.</p>
      ) : (
        <div className="space-y-5">
          {/* Auto-flag badges */}
          {data.flags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.flags.map((f) => {
                const meta = FLAG_LABEL[f];
                return (
                  <span
                    key={f}
                    title={meta.description}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border ${meta.tone}`}
                  >
                    {meta.label}
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-green-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> No setup issues detected.
            </div>
          )}

          {/* Setup snapshot */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Setup at a glance
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <SetupStat label="School name" value={data.setup.schoolNameSet ? "Set" : "Not set"} bad={!data.setup.schoolNameSet} />
              <SetupStat label="Classes" value={data.setup.classesCount} bad={data.setup.classesCount === 0} />
              <SetupStat label="Courses" value={data.setup.coursesCount} />
              <SetupStat label="Students" value={data.setup.studentsCount} />
              <SetupStat
                label="Duplicate suggestions"
                value={data.setup.duplicateStudentSuggestions}
                bad={data.setup.duplicateStudentSuggestions > 0}
              />
              <SetupStat
                label="Teachers w/o classes"
                value={data.setup.teachersWithNoClasses}
                bad={data.setup.teachersWithNoClasses > 0}
              />
            </div>
          </div>

          {/* Roster */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Roster ({data.teachers.length})
            </p>
            {data.teachers.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No teachers have joined yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <tr>
                      {([
                        { col: "name",               label: "Teacher",     align: "text-left"   },
                        { col: "schoolRole",         label: "Role",        align: "text-left"   },
                        { col: "subscriptionStatus", label: "Plan",        align: "text-left"   },
                        { col: "joinedAt",           label: "Joined",      align: "text-left"   },
                        { col: "lastSeenAt",         label: "Last seen",   align: "text-left"   },
                        { col: "lastRecordAt",       label: "Last record", align: "text-left"   },
                        { col: "lastDiaryAt",        label: "Last lesson", align: "text-left"   },
                        { col: "classesCount",       label: "Classes",     align: "text-center" },
                      ] as { col: RosterSortCol; label: string; align: string }[]).map(({ col, label, align }) => (
                        <th
                          key={col}
                          className={`px-3 py-2 ${align} cursor-pointer hover:text-gray-700 select-none whitespace-nowrap`}
                          onClick={() => toggleSort(col)}
                        >
                          <span className={`inline-flex items-center ${align === "text-center" ? "justify-center w-full" : ""}`}>
                            {label}
                            <SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedTeachers.map((t) => (
                      <tr key={t.userId} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5">
                          <button
                            type="button"
                            onClick={() => onJumpToUser(t.userId)}
                            className="font-medium text-gray-900 hover:text-primary hover:underline text-left"
                            title="Jump to this teacher in the All Users table"
                          >
                            {t.name || "—"}
                          </button>
                          {t.email && (
                            <p className="text-xs text-gray-500">{t.email}</p>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              t.schoolRole === "head_teacher"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {t.schoolRole === "head_teacher" ? "Head" : t.schoolRole === "support_staff" ? "Support" : "Teacher"}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              SUB_BADGE[t.subscriptionStatus] ?? SUB_BADGE.free
                            }`}
                          >
                            {fmtStatus(t.subscriptionStatus)}
                          </span>
                          {t.subscriptionStatus === "trialing" && t.subscriptionEnds && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              ends {fmtDateTime(t.subscriptionEnds)}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                          {fmtDateTime(t.joinedAt)}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                          {fmtDateTime(t.lastSeenAt)}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                          {fmtDateTime(t.lastRecordAt)}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                          {fmtDateTime(t.lastDiaryAt)}
                        </td>
                        <td className="px-3 py-2.5 text-center text-gray-700 font-mono">
                          {t.classesCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SetupStat({
  label,
  value,
  bad,
}: {
  label: string;
  value: string | number;
  bad?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        bad ? "border-rose-200 bg-rose-50" : "border-gray-200 bg-white"
      }`}
    >
      <p className={`text-lg font-bold ${bad ? "text-rose-700" : "text-gray-900"}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

function CopyableCode({ label, value, className }: { label: string; value: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="font-mono inline-flex items-center gap-1 hover:text-primary transition-colors"
      title="Copy"
    >
      <span className={className}>{label}</span>
      {copied ? <CheckCircle2 className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3 text-gray-300" />}
    </button>
  );
}

function MintSchoolDialog({
  onClose,
  onMinted,
}: {
  onClose: () => void;
  onMinted: (school: AdminSchool) => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [seatLimit, setSeatLimit] = useState(20);
  const [domainsText, setDomainsText] = useState("");
  const [domainLockEnabled, setDomainLockEnabled] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const mint = useMutation({
    mutationFn: (body: MintSchoolPayload) =>
      customFetch<AdminSchool>("/api/admin/schools", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: onMinted,
    onError: (err: unknown) => {
      setErrorMsg(extractApiError(err, "Mint failed"));
    },
  });

  const handleSubmit = () => {
    setErrorMsg(null);
    const allowedEmailDomains = domainsText
      .split(/[\s,\n]+/)
      .map((d) => d.trim())
      .filter(Boolean);
    if (domainLockEnabled && allowedEmailDomains.length === 0) {
      setErrorMsg("Add at least one allowed email domain before turning on the lock.");
      return;
    }
    mint.mutate({ displayName: displayName.trim(), seatLimit, allowedEmailDomains, domainLockEnabled });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mint a new school</DialogTitle>
          <DialogDescription>
            ClassTrack will generate a unique join code and head-teacher PIN. They're shown once after creation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="ms-name" className="text-sm">Display name</Label>
            <Input
              id="ms-name"
              autoFocus
              placeholder="Riverside Public School"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="ms-seats" className="text-sm">Seat limit</Label>
            <Input
              id="ms-seats"
              type="number"
              min={1}
              max={10000}
              value={seatLimit}
              onChange={(e) => setSeatLimit(parseInt(e.target.value || "0", 10))}
              className="mt-1.5"
            />
            <p className="text-xs text-gray-400 mt-1">Maximum teachers who can join with this code.</p>
          </div>
          <div>
            <Label htmlFor="ms-domains" className="text-sm">Allowed email domains (optional)</Label>
            <Input
              id="ms-domains"
              placeholder="riverside.nsw.edu.au, *.det.nsw.edu.au"
              value={domainsText}
              onChange={(e) => setDomainsText(e.target.value)}
              className="mt-1.5 font-mono text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Comma-separated. Use <code>*.example.edu.au</code> to allow subdomains.</p>
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <div>
              <Label htmlFor="ms-lock" className="text-sm font-medium">Lock to listed domains</Label>
              <p className="text-xs text-gray-400">Only verified emails matching the list can join.</p>
            </div>
            <Switch id="ms-lock" checked={domainLockEnabled} onCheckedChange={setDomainLockEnabled} />
          </div>
          {errorMsg && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
              <p className="text-xs text-rose-800">{errorMsg}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!displayName.trim() || seatLimit < 1 || mint.isPending}
            className="gap-1.5"
          >
            {mint.isPending && <RefreshCw className="h-4 w-4 animate-spin" />}
            Mint school
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MintedSchoolReveal({ school, onClose }: { school: AdminSchool; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-teal-600" />
            School minted
          </DialogTitle>
          <DialogDescription>
            Send these credentials to the school's head teacher. The PIN is shown only here — copy it now.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-3">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Display name</p>
            <p className="text-sm font-medium text-gray-900">{school.displayName}</p>
          </div>
          <div className="rounded-lg border border-teal-200 bg-teal-50 p-3">
            <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-1">Join code</p>
            <CopyableCode label={school.schoolCode} value={school.schoolCode} className="text-lg font-bold tracking-widest text-teal-900" />
          </div>
          {school.headTeacherPin ? (
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
              <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-1">Head teacher PIN</p>
              <CopyableCode label={school.headTeacherPin} value={school.headTeacherPin} className="text-lg font-bold tracking-widest text-violet-900" />
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
              PIN unavailable — this dialog should only open from the mint flow.
            </div>
          )}
          <div className="text-xs text-gray-500 grid grid-cols-2 gap-2 pt-2">
            <div><span className="text-gray-400">Seats:</span> {school.seatLimit ?? "—"}</div>
            <div><span className="text-gray-400">Lock:</span> {school.domainLockEnabled ? "On" : "Off"}</div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditSchoolDialog({
  school,
  onClose,
  onSaved,
}: {
  school: AdminSchool;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(school.displayName);
  const [seatLimit, setSeatLimit] = useState(school.seatLimit ?? 20);
  const [domainsText, setDomainsText] = useState(school.allowedEmailDomains.join(", "));
  const [domainLockEnabled, setDomainLockEnabled] = useState(school.domainLockEnabled);
  const [status, setStatus] = useState<"active" | "revoked">(school.status);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Reset local form whenever a different school is opened.
  useEffect(() => {
    setDisplayName(school.displayName);
    setSeatLimit(school.seatLimit ?? 20);
    setDomainsText(school.allowedEmailDomains.join(", "));
    setDomainLockEnabled(school.domainLockEnabled);
    setStatus(school.status);
    setErrorMsg(null);
  }, [school]);

  const save = useMutation({
    mutationFn: (body: PatchSchoolPayload) =>
      customFetch<AdminSchool>(`/api/admin/schools/${encodeURIComponent(school.schoolCode)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: onSaved,
    onError: (err: unknown) => {
      setErrorMsg(extractApiError(err, "Save failed"));
    },
  });

  const handleSubmit = () => {
    setErrorMsg(null);
    const allowedEmailDomains = domainsText
      .split(/[\s,\n]+/)
      .map((d) => d.trim())
      .filter(Boolean);
    save.mutate({
      displayName: displayName.trim(),
      seatLimit,
      allowedEmailDomains,
      domainLockEnabled,
      status,
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {school.displayName}</DialogTitle>
          <DialogDescription>
            Code <code className="font-mono">{school.schoolCode}</code> · {school.teacherCount} teacher{school.teacherCount === 1 ? "" : "s"} joined
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="es-name" className="text-sm">Display name</Label>
            <Input
              id="es-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="es-seats" className="text-sm">Seat limit</Label>
            <Input
              id="es-seats"
              type="number"
              min={Math.max(1, school.teacherCount)}
              max={10000}
              value={seatLimit}
              onChange={(e) => setSeatLimit(parseInt(e.target.value || "0", 10))}
              className="mt-1.5"
            />
            <p className="text-xs text-gray-400 mt-1">
              Currently {school.teacherCount} of {school.seatLimit ?? "—"} used. Cannot drop below current usage.
            </p>
          </div>
          <div>
            <Label htmlFor="es-domains" className="text-sm">Allowed email domains</Label>
            <Input
              id="es-domains"
              placeholder="riverside.nsw.edu.au, *.det.nsw.edu.au"
              value={domainsText}
              onChange={(e) => setDomainsText(e.target.value)}
              className="mt-1.5 font-mono text-sm"
            />
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <div>
              <Label htmlFor="es-lock" className="text-sm font-medium">Lock to listed domains</Label>
            </div>
            <Switch id="es-lock" checked={domainLockEnabled} onCheckedChange={setDomainLockEnabled} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="es-status" className="text-sm font-medium flex items-center gap-1.5">
                {status === "active" ? <ShieldCheck className="h-3.5 w-3.5 text-green-600" /> : <ShieldOff className="h-3.5 w-3.5 text-rose-600" />}
                {status === "active" ? "Active" : "Revoked"}
              </Label>
              <p className="text-xs text-gray-400">Revoked codes block new joins. Existing teachers keep access.</p>
            </div>
            <Switch
              id="es-status"
              checked={status === "active"}
              onCheckedChange={(v) => setStatus(v ? "active" : "revoked")}
            />
          </div>
          {errorMsg && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
              <p className="text-xs text-rose-800">{errorMsg}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={save.isPending} className="gap-1.5">
            {save.isPending && <RefreshCw className="h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Task #138 — Cost & Features tabs ────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatCommentTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function RecentAiCommentsPanel() {
  const { data, isLoading, error } = useAdminRecentAiComments();

  const grouped = (() => {
    const map = new Map<string, { id: number; comment: string; createdAt: string }[]>();
    for (const row of data ?? []) {
      const list = map.get(row.type) ?? [];
      list.push({ id: row.id, comment: row.comment, createdAt: row.createdAt });
      map.set(row.type, list);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  })();

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <h3 className="font-semibold text-gray-900 mb-1">Recent thumbs-down comments</h3>
      <p className="text-xs text-gray-500 mb-3">
        Last 50 free-text comments teachers left when rating an AI output 👎,
        grouped by feature.
      </p>
      {isLoading ? (
        <div className="text-sm text-gray-500">Loading comments…</div>
      ) : error ? (
        <div className="text-sm text-red-600">
          Failed to load: {(error as Error).message}
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-sm text-gray-400 py-6 text-center">
          No thumbs-down comments yet.
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto space-y-4 pr-1">
          {grouped.map(([type, rows]) => (
            <div key={type}>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                {type} <span className="text-gray-400">({rows.length})</span>
              </div>
              <ul className="space-y-2">
                {rows.map((r) => (
                  <li
                    key={r.id}
                    className="border border-gray-100 rounded-lg p-3 bg-gray-50"
                  >
                    <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                      {r.comment}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {formatCommentTimestamp(r.createdAt)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CostTab() {
  const { data, isLoading, error } = useAdminCostOverview();
  if (isLoading) return <div className="text-sm text-gray-500">Loading cost data…</div>;
  if (error) return <div className="text-sm text-red-600">Failed to load: {(error as Error).message}</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">AI calls — {data.monthKey}</div>
          <div className="text-2xl font-bold mt-1">{data.aiCallsThisMonth.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Estimated AI cost</div>
          <div className="text-2xl font-bold mt-1">${data.estimatedAiCostUsd.toFixed(2)}</div>
          <div className="text-xs text-gray-400 mt-0.5">tokens × per-model pricing — see lib/ai-cost.ts</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Total image storage</div>
          <div className="text-2xl font-bold mt-1">{formatBytes(data.totalImageBytes)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Top user (storage)</div>
          <div className="text-sm font-medium mt-1 truncate">{data.topImageStorageUsers[0]?.name ?? "—"}</div>
          <div className="text-xs text-gray-500">{formatBytes(data.topImageStorageUsers[0]?.bytes ?? 0)}</div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 mb-3">AI calls by type</h3>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-gray-500 border-b">
            <tr><th className="text-left py-2">Type</th><th className="text-right">Total</th><th className="text-right">Success</th><th className="text-right">Fail</th><th className="text-right">Est. cost</th><th className="text-right">Feedback</th></tr>
          </thead>
          <tbody>
            {data.aiCallsByType.map((r) => (
              <tr key={r.type} className="border-b last:border-0">
                <td className="py-2">{r.type}</td>
                <td className="text-right">{r.callCount.toLocaleString()}</td>
                <td className="text-right text-green-600">{r.successCount.toLocaleString()}</td>
                <td className="text-right text-red-600">{r.failCount.toLocaleString()}</td>
                <td className="text-right">${r.estimatedCostUsd.toFixed(2)}</td>
                <td className="text-right">
                  {r.feedbackTotal === 0 ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <span>
                      <span className="text-green-600">👍 {r.feedbackUpCount}</span>
                      <span className="mx-1 text-gray-300">·</span>
                      <span className="text-red-600">👎 {r.feedbackDownCount}</span>
                      {r.feedbackUpPct !== null && (
                        <span className="ml-2 text-xs text-gray-500">({r.feedbackUpPct}%👍)</span>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {data.aiCallsByType.length === 0 && (
              <tr><td colSpan={6} className="py-4 text-center text-gray-400">No AI calls this month yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <RecentAiCommentsPanel />

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Top AI users — {data.monthKey}</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr><th className="text-left py-2">User</th><th className="text-right">Calls</th><th className="text-right">Fail</th></tr>
            </thead>
            <tbody>
              {data.topAiUsers.map((u) => (
                <tr key={u.userId} className="border-b last:border-0">
                  <td className="py-2 font-mono text-xs truncate max-w-[180px]">{u.userId}</td>
                  <td className="text-right">{u.callCount.toLocaleString()}</td>
                  <td className="text-right text-red-600">{u.failCount.toLocaleString()}</td>
                </tr>
              ))}
              {data.topAiUsers.length === 0 && <tr><td colSpan={3} className="py-4 text-center text-gray-400">—</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Top image storage</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr><th className="text-left py-2">User</th><th className="text-right">Bytes</th></tr>
            </thead>
            <tbody>
              {data.topImageStorageUsers.map((u) => (
                <tr key={u.userId} className="border-b last:border-0">
                  <td className="py-2 truncate max-w-[220px]">{u.name ?? <span className="font-mono text-xs">{u.userId}</span>}</td>
                  <td className="text-right">{formatBytes(u.bytes)}</td>
                </tr>
              ))}
              {data.topImageStorageUsers.length === 0 && <tr><td colSpan={2} className="py-4 text-center text-gray-400">—</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FeaturesTab() {
  const counters = useAdminFeatureCounters();
  const requests = useAdminFeatureRequests();

  // Pivot: rows = day, columns = feature.
  const dailyRows = counters.data?.daily ?? [];
  const monthlyRows = counters.data?.monthly ?? [];
  const days = Array.from(new Set(dailyRows.map((r) => r.day))).sort().reverse();
  const features = Array.from(new Set(dailyRows.map((r) => r.feature))).sort();
  const cell = (day: string, feature: string) =>
    dailyRows.find((r) => r.day === day && r.feature === feature);

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 mb-3">
          Distinct teachers this month{counters.data?.monthKey ? ` — ${counters.data.monthKey}` : ""}
        </h3>
        {counters.isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {monthlyRows.map((m) => (
              <div key={m.feature} className="border border-gray-200 rounded-lg p-3">
                <div className="text-xs uppercase tracking-wide text-gray-500">{m.feature}</div>
                <div className="text-2xl font-bold mt-1">{m.distinct_teachers}</div>
                <div className="text-xs text-gray-500">{m.total_uses.toLocaleString()} total uses</div>
              </div>
            ))}
            {monthlyRows.length === 0 && (
              <div className="col-span-full text-sm text-gray-400">No usage tracked yet this month.</div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Per-feature daily usage (last 14 days)</h3>
        {counters.isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : counters.error ? (
          <div className="text-sm text-red-600">Failed to load.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2">Day</th>
                {features.map((f) => <th key={f} className="text-right">{f}</th>)}
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d} className="border-b last:border-0">
                  <td className="py-2 font-mono text-xs">{d}</td>
                  {features.map((f) => {
                    const c = cell(d, f);
                    return (
                      <td key={f} className="text-right">
                        {c ? (
                          <span>
                            {c.count.toLocaleString()}
                            <span className="text-gray-400 text-xs ml-1">({c.distinct_users}u)</span>
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {days.length === 0 && (
                <tr><td colSpan={features.length + 1} className="py-4 text-center text-gray-400">No usage tracked yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Recent feature requests</h3>
        {requests.isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : requests.error ? (
          <div className="text-sm text-red-600">Failed to load.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {(requests.data ?? []).map((r) => (
              <li key={r.id} className="py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-xs text-gray-500">
                    {r.email ?? <span className="font-mono">{r.userId}</span>}
                  </div>
                  <div className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleString()}</div>
                </div>
                <div className="text-sm text-gray-800 whitespace-pre-wrap mt-1">{r.body}</div>
              </li>
            ))}
            {(requests.data ?? []).length === 0 && (
              <li className="py-4 text-center text-gray-400 text-sm">No requests yet.</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Task #145 — Deleted accounts tab ────────────────────────────────────────
// Lists every profile with `deletion_requested_at IS NOT NULL`, oldest
// request first. Each row has a Restore button that posts to
// `/api/admin/restore-account`. 410 Gone (already purged or window
// expired) is surfaced via a toast.

function DeletedAccountsTab() {
  const { data, isLoading, error, refetch } = useAdminDeletedAccounts();
  const restore = useRestoreAccount();
  const { toast } = useToast();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleRestore = (acc: AdminDeletedAccount) => {
    setPendingId(acc.userId);
    restore.mutate(acc.userId, {
      onSuccess: () => {
        toast({
          title: "Account restored",
          description: `${acc.name ?? acc.email ?? acc.userId} can sign in again.`,
        });
        setPendingId(null);
      },
      onError: (err: unknown) => {
        const msg = (err as ApiError | undefined)?.status === 410
          ? "The 30-day window has passed — the account is permanently deleted."
          : extractApiError(err, "Could not restore the account.");
        toast({ title: "Restore failed", description: msg, variant: "destructive" });
        setPendingId(null);
      },
    });
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading deleted accounts…</div>;
  }
  if (error) {
    return (
      <div className="text-sm text-red-600">
        Failed to load: {(error as Error).message}
      </div>
    );
  }
  const rows = data ?? [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-900">Deleted accounts</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {rows.length === 0
              ? "No accounts are currently in the 30-day grace window."
              : `${rows.length} account${rows.length === 1 ? "" : "s"} pending permanent deletion. Oldest first.`}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">School</th>
                <th className="px-4 py-3 text-left whitespace-nowrap">Requested</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">Days left</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const days = r.daysRemaining ?? 0;
                const tone =
                  days <= 3 ? "bg-rose-100 text-rose-700"
                  : days <= 7 ? "bg-amber-100 text-amber-700"
                  : "bg-gray-100 text-gray-700";
                const isPending = pendingId === r.userId && restore.isPending;
                return (
                  <tr key={r.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 min-w-[220px]">
                      <div className="font-medium text-gray-900">{r.name ?? "—"}</div>
                      {r.email && (
                        <div className="text-xs text-gray-500 mt-0.5">{r.email}</div>
                      )}
                      <div className="text-xs text-gray-300 font-mono mt-0.5">{r.userId}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {r.schoolCode ? (
                        <span className="px-1.5 py-0.5 rounded font-mono bg-blue-50 text-blue-600">
                          {r.schoolCode}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                      {r.schoolRole && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {r.schoolRole === "head_teacher" ? "Head Teacher" : "Teacher"}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {fmtDate(r.deletionRequestedAt)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}>
                        {days} day{days === 1 ? "" : "s"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestore(r)}
                        disabled={isPending}
                        className="gap-1.5"
                      >
                        {isPending && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                        Restore
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type WaitlistLead = {
  id: number;
  name: string;
  email: string;
  school: string | null;
  role: string | null;
  stateRegion: string | null;
  schoolType: string | null;
  interestedFeature: string | null;
  currentTool: string | null;
  marketingConsent: boolean;
  consentAt: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  referrer: string | null;
  leadStatus: string;
  createdAt: string;
};

function WaitlistLeadsTab() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "waitlist"],
    queryFn: () => customFetch<{ leads: WaitlistLead[] }>("/api/waitlist/leads", { credentials: "include" }),
  });

  const leads = data?.leads ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Waitlist Leads</h2>
          <p className="text-sm text-gray-500 mt-0.5">{leads.length} total</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>Refresh</Button>
          <a
            href="/api/waitlist/leads.csv"
            download
            className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium"
          >
            Export CSV
          </a>
        </div>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && <p className="text-sm text-red-600">Failed to load waitlist leads.</p>}

      {!isLoading && !isError && leads.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">No leads yet.</div>
      )}

      {leads.length > 0 && (
        <div className="rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2.5 font-medium text-gray-600 whitespace-nowrap">Name</th>
                <th className="px-4 py-2.5 font-medium text-gray-600">Email</th>
                <th className="px-4 py-2.5 font-medium text-gray-600 hidden md:table-cell whitespace-nowrap">School</th>
                <th className="px-4 py-2.5 font-medium text-gray-600 hidden md:table-cell whitespace-nowrap">State</th>
                <th className="px-4 py-2.5 font-medium text-gray-600 hidden lg:table-cell whitespace-nowrap">Role</th>
                <th className="px-4 py-2.5 font-medium text-gray-600 hidden lg:table-cell whitespace-nowrap">School type</th>
                <th className="px-4 py-2.5 font-medium text-gray-600 hidden xl:table-cell whitespace-nowrap">Interested in</th>
                <th className="px-4 py-2.5 font-medium text-gray-600 hidden xl:table-cell whitespace-nowrap">Mktg</th>
                <th className="px-4 py-2.5 font-medium text-gray-600 whitespace-nowrap">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {leads.map((l) => (
                <tr key={l.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{l.name}</td>
                  <td className="px-4 py-3 text-gray-700">
                    <a href={`mailto:${l.email}`} className="hover:underline text-primary">{l.email}</a>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell max-w-[180px] truncate">{l.school ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell whitespace-nowrap">{l.stateRegion ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell whitespace-nowrap">{l.role ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell whitespace-nowrap">{l.schoolType ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-500 hidden xl:table-cell max-w-[160px] truncate">{l.interestedFeature ?? "—"}</td>
                  <td className="px-4 py-3 hidden xl:table-cell text-center">
                    {l.marketingConsent
                      ? <span className="text-emerald-600 font-bold">✓</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {new Date(l.createdAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminPageInner() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const [userSort, setUserSort] = useState<{ col: string; dir: "asc" | "desc" }>({
    col: "activity_score", dir: "desc",
  });
  const [activeTab, setActiveTab] = useState<"overview" | "schools" | "cost" | "features" | "deleted" | "waitlist">("overview");

  /** Switch to the Overview tab and scroll the matching `#user-{id}` row
   *  into view. The tab switch is sync, so we defer the scroll one frame
   *  to let React render the table before we measure it. */
  const jumpToUser = (userId: string) => {
    setActiveTab("overview");
    requestAnimationFrame(() => {
      const el = document.getElementById(`user-${userId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  const adminCheck = useAdminCheck(user?.id);
  const isAdmin = adminCheck.data?.isAdmin === true;

  const overview     = useAdminOverview();
  const users        = useAdminUsers();
  const signups      = useAdminSignups();
  const activity     = useAdminActivity();
  const recordTypes  = useAdminRecordTypes();
  const subscriptions = useAdminSubscriptions();
  const adoption     = useAdminFeatureAdoption();
  const deleteUser   = useDeleteUser();
  const grantBeta    = useGrantBetaAccess();
  const revokeBeta   = useRevokeBetaAccess();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  function handleDeleteUser() {
    if (!deleteTarget) return;
    deleteUser.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["admin"] });
  };

  const ov = overview.data;
  const adopt = adoption.data;

  // Feature adoption chart data
  const adoptionData = adopt ? [
    { name: "Diary",    used: adopt.used_diary,    total: adopt.total_users },
    { name: "Records",  used: adopt.used_records,  total: adopt.total_users },
    { name: "Tasks",    used: adopt.used_tasks,    total: adopt.total_users },
    { name: "Courses",  used: adopt.used_courses,  total: adopt.total_users },
    { name: "Students", used: adopt.used_students, total: adopt.total_users },
  ] : [];

  const adoptionPct = adoptionData.map((d) => ({
    ...d,
    pct: d.total > 0 ? Math.round((d.used / d.total) * 100) : 0,
  }));

  // Subscription status chart
  const subData = subscriptions.data?.statusBreakdown ?? [];
  const subPlanData = subscriptions.data?.planBreakdown ?? [];

  // Record types chart
  const recTypeData = recordTypes.data ?? [];

  // Signups / activity merged chart
  const signupMap = new Map((signups.data ?? []).map((d) => [d.date?.slice(0, 10), d.count]));
  const activityMap = new Map((activity.data ?? []).map((d) => [d.date?.slice(0, 10), d.diary_entries]));
  const last30 = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    const key = d.toISOString().slice(0, 10);
    return {
      date: d.toLocaleDateString("en-AU", { day: "numeric", month: "short" }),
      signups: signupMap.get(key) ?? 0,
      entries: activityMap.get(key) ?? 0,
    };
  });

  // User table — sort
  const sortedUsers = [...(users.data ?? [])].sort((a, b) => {
    const col = userSort.col as keyof typeof a;
    const va = a[col] ?? 0;
    const vb = b[col] ?? 0;
    if (va < vb) return userSort.dir === "asc" ? -1 : 1;
    if (va > vb) return userSort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (col: string) => {
    setUserSort((s) =>
      s.col === col
        ? { col, dir: s.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "desc" }
    );
  };

  const isLoading = overview.isLoading;

  // Guard: wait for admin check, then redirect or show denied
  if (adminCheck.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-500 text-sm">Checking access…</p>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-gray-700 font-medium">You don't have admin access.</p>
        <Button variant="outline" onClick={() => setLocation("/app/dashboard")}>Back to dashboard</Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-screen-2xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-sm text-gray-500 mt-0.5">Platform metrics and user insights</p>
        </div>
        <Button variant="outline" size="sm" onClick={refreshAll} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* ── Tab nav (Task #36) ─────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 -mt-2">
        <nav className="flex gap-6">
          {([
            { id: "overview", label: "Overview" },
            { id: "schools",  label: "Schools"  },
            { id: "cost",     label: "Cost"     },
            { id: "features", label: "Features" },
            { id: "deleted",  label: "Deleted"  },
            { id: "waitlist", label: "Waitlist" },
          ] as const).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`pb-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.id
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "overview" && <>
      {/* ── Overview stats ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
        <StatCard icon={Users}       label="Total Users"     value={ov?.total_users ?? "—"}          sub={`+${ov?.new_users_7d ?? 0} this week`} />
        <StatCard icon={GraduationCap} label="Onboarded"     value={ov?.onboarded_users ?? "—"}      color="text-green-600" />
        <StatCard icon={CreditCard}  label="Paid Users"      value={ov?.paid_users ?? "—"}           color="text-blue-600" />
        <StatCard icon={BookOpen}    label="Diary Entries"   value={ov?.total_diary_entries ?? "—"}  color="text-violet-600" />
        <StatCard icon={FileBox}     label="Records"         value={ov?.total_records ?? "—"}        color="text-amber-600" />
        <StatCard icon={CheckSquare} label="Tasks"           value={ov?.total_tasks ?? "—"}          color="text-teal-600" />
      </div>

      {/* ── Charts row ─────────────────────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">

        {/* Signups & Activity over time */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-4">Signups & Diary Activity — Last 30 Days</h2>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={last30} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={4} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="signups" stroke="#1cb08a" strokeWidth={2} dot={false} name="Signups" />
              <Line type="monotone" dataKey="entries" stroke="#378add" strokeWidth={2} dot={false} name="Diary entries" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Subscription status */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-4">Subscription Status</h2>
          {subData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={subData}
                  dataKey="user_count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ status, user_count }) => `${fmtStatus(status)} (${user_count})`}
                  labelLine={false}
                >
                  {subData.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(val, name) => [val, name]} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
              No subscription data yet
            </div>
          )}
          {subPlanData.length > 0 && (
            <div className="mt-3 flex gap-3 justify-center text-xs">
              {subPlanData.map((p: any) => (
                <span key={p.billing_interval} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-primary inline-block" />
                  {p.billing_interval === "month" ? "Monthly" : "Annual"}: {p.count}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Feature adoption & Record types ────────────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-6">

        {/* Feature adoption */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-1">Feature Adoption</h2>
          <p className="text-xs text-gray-400 mb-4">% of users who have used each feature</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={adoptionPct} layout="vertical" margin={{ left: 10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={65} />
              <Tooltip formatter={(v) => [`${v}%`, "Adoption"]} />
              <Bar dataKey="pct" fill="#1cb08a" radius={[0, 4, 4, 0]}>
                {adoptionPct.map((_: any, i: number) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Record types */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-1">Record Types</h2>
          <p className="text-xs text-gray-400 mb-4">Breakdown of all records created</p>
          {recTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={recTypeData} margin={{ left: -10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {recTypeData.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">
              No records yet
            </div>
          )}
        </div>
      </div>

      {/* ── User table ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-800">All Users</h2>
            <p className="text-xs text-gray-400 mt-0.5">{sortedUsers.length} users — click column headers to sort</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                {[
                  { col: "name",                  label: "User"           },
                  { col: "subscription_status",   label: "Plan"           },
                  { col: "records",               label: "Records"        },
                  { col: "classes",               label: "Classes"        },
                  { col: "students",              label: "Students"       },
                  { col: "ai_calls_this_month",   label: "AI (this mo)"   },
                  { col: "ai_calls_last_month",   label: "AI (last mo)"   },
                  { col: "ai_cost_this_month_usd", label: "Cost (USD)"    },
                  { col: "image_storage_bytes",   label: "Images (MB)"    },
                  { col: "activity_score",        label: "Activity"       },
                  { col: "last_activity",         label: "Last Active"    },
                  { col: "created_at",            label: "Joined"         },
                ].map(({ col, label }) => (
                  <th
                    key={col}
                    className="px-4 py-3 text-left cursor-pointer hover:text-gray-700 select-none whitespace-nowrap"
                    onClick={() => toggleSort(col)}
                  >
                    <span className="flex items-center">
                      {label}
                      <SortIcon col={col} sortCol={userSort.col} sortDir={userSort.dir} />
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3 text-left whitespace-nowrap">Beta</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.isLoading ? (
                <tr><td colSpan={14} className="text-center py-8 text-gray-400">Loading…</td></tr>
              ) : sortedUsers.length === 0 ? (
                <tr><td colSpan={14} className="text-center py-8 text-gray-400">No users yet</td></tr>
              ) : (
                sortedUsers.map((u: any) => (
                  <tr key={u.user_id} id={`user-${u.user_id}`} className="hover:bg-gray-50 transition-colors target:bg-blue-50">
                    <td className="px-4 py-3 min-w-[220px]">
                      <div className="font-medium text-gray-900">{u.name || "—"}</div>
                      {u.email && <div className="text-xs text-gray-500 mt-0.5">{u.email}</div>}
                      {u.state && <div className="text-xs text-gray-400 mt-0.5">{u.state}{u.teaching_level ? ` · ${u.teaching_level}` : ""}</div>}
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {u.school_role && (
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${u.school_role === "head_teacher" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600"}`}>
                            {u.school_role === "head_teacher" ? "Head Teacher" : "Teacher"}
                          </span>
                        )}
                        {u.school_code && (
                          <span className="px-1.5 py-0.5 rounded text-xs font-mono bg-blue-50 text-blue-600">
                            {u.school_code}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-300 font-mono mt-0.5">{u.user_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SUB_BADGE[u.subscription_status] ?? SUB_BADGE.free}`}>
                        {fmtStatus(u.subscription_status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-gray-700">{u.records}</td>
                    <td className="px-4 py-3 text-center font-mono text-gray-700">{u.classes ?? 0}</td>
                    <td className="px-4 py-3 text-center font-mono text-gray-700">{u.students}</td>
                    <td className="px-4 py-3 text-center font-mono text-gray-700">{u.ai_calls_this_month ?? 0}</td>
                    <td className="px-4 py-3 text-center font-mono text-gray-400">{u.ai_calls_last_month ?? 0}</td>
                    <td className="px-4 py-3 text-center font-mono text-gray-700">
                      ${Number(u.ai_cost_this_month_usd ?? 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-gray-700">
                      {(Number(u.image_storage_bytes ?? 0) / (1024 * 1024)).toFixed(1)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${u.activity_score > 50 ? "text-green-600" : u.activity_score > 10 ? "text-amber-600" : "text-gray-400"}`}>
                        {u.activity_score}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {fmtDate(u.last_activity ?? u.last_diary_entry ?? u.last_record ?? u.last_task)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {u.beta_access_until && new Date(u.beta_access_until) > new Date() ? (
                        <div className="flex items-center gap-1.5">
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            ✓ Until {new Date(u.beta_access_until).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" })}
                          </span>
                          <button
                            title="Revoke beta access"
                            onClick={() => revokeBeta.mutate(u.user_id)}
                            disabled={revokeBeta.isPending}
                            className="text-xs text-red-400 hover:text-red-600 hover:underline disabled:opacity-50"
                          >
                            Revoke
                          </button>
                        </div>
                      ) : (
                        <button
                          title="Grant beta access until 2027-01-01"
                          onClick={() => grantBeta.mutate(u.user_id)}
                          disabled={grantBeta.isPending}
                          className="text-xs text-indigo-500 hover:text-indigo-700 hover:underline disabled:opacity-50"
                        >
                          Grant
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        title="Delete user"
                        onClick={() => setDeleteTarget({ id: u.user_id, name: u.name || u.user_id })}
                        className="p-1.5 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Improvement signals ────────────────────────────────────────────── */}
      {!isLoading && adopt && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-5">
          <h2 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Improvement Signals
          </h2>
          <ul className="space-y-2 text-sm text-blue-800">
            {adopt.used_diary < adopt.total_users && (
              <li>• <strong>{adopt.total_users - adopt.used_diary}</strong> user(s) signed up but haven't used the diary yet — consider an onboarding prompt.</li>
            )}
            {adopt.used_records < Math.floor(adopt.total_users * 0.5) && (
              <li>• Records adoption is below 50% — teachers may not know about NCCD compliance tools. Consider a tooltip or tour.</li>
            )}
            {adopt.used_courses === 0 && (
              <li>• No users have set up courses yet — the setup flow might need to surface this earlier.</li>
            )}
            {adopt.total_users > 0 && adopt.used_diary === adopt.total_users && (
              <li>• Diary adoption is 100% — great engagement. Consider prompting active diary users to try Records.</li>
            )}
          </ul>
        </div>
      )}

      </>}

      {activeTab === "schools" && <>
      {/* ── Schools Management (Task #44) ──────────────────────────────────── */}
      <SchoolsAdminPanel />

      {/* ── School Troubleshooting (Task #36) ─────────────────────────────── */}
      <SchoolTroubleshootingPanelLoader onJumpToUser={jumpToUser} />
      </>}

      {activeTab === "cost" && <CostTab />}
      {activeTab === "features" && <FeaturesTab />}
      {activeTab === "deleted" && <DeletedAccountsTab />}
      {activeTab === "waitlist" && <WaitlistLeadsTab />}

      {/* ── Delete user confirmation modal ─────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-6 max-w-sm w-full mx-4">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete user?</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  This will permanently delete <span className="font-medium text-gray-700">{deleteTarget.name}</span> and all of their data. This cannot be undone.
                </p>
              </div>
            </div>
            {deleteUser.isError && (
              <p className="text-sm text-red-600 mb-3">
                {(deleteUser.error as any)?.message ?? "Something went wrong"}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setDeleteTarget(null); deleteUser.reset(); }}
                disabled={deleteUser.isPending}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleteUser.isPending}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {deleteUser.isPending ? "Deleting…" : "Delete user"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
